import { jcToTextAlign, twipsToPx } from "../units";
import {
  firstChildNS,
  wAttr,
  W_NS,
} from "./xmlUtils";

export interface CellBorderCss {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

export interface OoxmlTableCellMeta {
  colSpan: number;
  rowSpan: number;
  widthPx?: number;
  widthPercent?: number;
  textAlign?: "left" | "center" | "right" | "justify";
  verticalAlign?: "top" | "middle" | "bottom";
  backgroundColor?: string;
  borders?: CellBorderCss;
  isHeader?: boolean;
}

export interface OoxmlTableRowMeta {
  isHeader: boolean;
  heightPx?: number;
  heightRule?: "exact" | "atLeast";
  cells: OoxmlTableCellMeta[];
}

export interface OoxmlTableMeta {
  index: number;
  widthPercent?: number;
  widthPx?: number;
  /** Word `w:tblLayout/@type` — fixed geometry vs auto-fit. */
  layoutMode?: "fixed" | "autofit";
  /** Column widths from `w:tblGrid/w:gridCol` (px). Authoritative for proportions. */
  gridColWidthsPx?: number[];
  borders?: CellBorderCss;
  rows: OoxmlTableRowMeta[];
}

function borderSideCss(sideEl: Element | null): string | null {
  if (!sideEl) return null;
  const val = wAttr(sideEl, "val");
  if (!val || val === "nil" || val === "none") return null;

  const sz = wAttr(sideEl, "sz");
  const widthPx =
    sz && /^\d+$/.test(sz)
      ? Math.max(1, Math.round(parseInt(sz, 10) / 8 / 0.75))
      : 1;

  const color = wAttr(sideEl, "color");
  const colorCss =
    color && color !== "auto" && /^[0-9a-fA-F]{6}$/.test(color)
      ? `#${color}`
      : "#000000";

  return `${widthPx}px solid ${colorCss}`;
}

function readCellBorders(tcPr: Element | null): CellBorderCss | undefined {
  if (!tcPr) return undefined;
  const bordersEl = firstChildNS(tcPr, "tcBorders");
  if (!bordersEl) return undefined;

  const borders: CellBorderCss = {};
  const top = borderSideCss(firstChildNS(bordersEl, "top"));
  const right = borderSideCss(firstChildNS(bordersEl, "right"));
  const bottom = borderSideCss(firstChildNS(bordersEl, "bottom"));
  const left = borderSideCss(firstChildNS(bordersEl, "left"));

  if (top) borders.top = top;
  if (right) borders.right = right;
  if (bottom) borders.bottom = bottom;
  if (left) borders.left = left;

  return Object.keys(borders).length ? borders : undefined;
}

function readTableBorders(tblPr: Element | null): CellBorderCss | undefined {
  if (!tblPr) return undefined;
  const bordersEl = firstChildNS(tblPr, "tblBorders");
  if (!bordersEl) return undefined;

  const borders: CellBorderCss = {};
  const top = borderSideCss(firstChildNS(bordersEl, "top"));
  const right = borderSideCss(firstChildNS(bordersEl, "right"));
  const bottom = borderSideCss(firstChildNS(bordersEl, "bottom"));
  const left = borderSideCss(firstChildNS(bordersEl, "left"));
  const insideH = borderSideCss(firstChildNS(bordersEl, "insideH"));
  const insideV = borderSideCss(firstChildNS(bordersEl, "insideV"));

  if (top) borders.top = top;
  if (right) borders.right = right;
  if (bottom) borders.bottom = bottom;
  if (left) borders.left = left;
  if (insideH) {
    borders.top ??= insideH;
    borders.bottom ??= insideH;
  }
  if (insideV) {
    borders.left ??= insideV;
    borders.right ??= insideV;
  }

  return Object.keys(borders).length ? borders : undefined;
}

function readTableLayout(
  tblPr: Element | null,
): OoxmlTableMeta["layoutMode"] | undefined {
  if (!tblPr) return undefined;
  const layout = firstChildNS(tblPr, "tblLayout");
  const type = layout ? wAttr(layout, "type") : null;
  if (type === "fixed") return "fixed";
  if (type === "autofit") return "autofit";
  return undefined;
}

function readGridColWidths(tblEl: Element): number[] {
  const grid = firstChildNS(tblEl, "tblGrid");
  if (!grid) return [];

  const cols = grid.getElementsByTagNameNS(W_NS, "gridCol");
  const widths: number[] = [];
  for (let i = 0; i < cols.length; i++) {
    const wVal = wAttr(cols[i], "w");
    if (wVal && /^\d+$/.test(wVal)) {
      widths.push(twipsToPx(parseInt(wVal, 10)));
    }
  }
  return widths;
}

function readTableWidth(tblPr: Element | null): {
  widthPx?: number;
  widthPercent?: number;
} {
  if (!tblPr) return {};
  const tblW = firstChildNS(tblPr, "tblW");
  if (!tblW) return {};

  const wVal = wAttr(tblW, "w");
  const wType = wAttr(tblW, "type") ?? "dxa";
  if (!wVal || !/^\d+$/.test(wVal)) return {};

  const n = parseInt(wVal, 10);
  if (wType === "pct") return { widthPercent: Math.round(n / 50) };
  if (wType === "dxa") return { widthPx: twipsToPx(n) };
  return {};
}

function readCellWidth(tcPr: Element | null): {
  widthPx?: number;
  widthPercent?: number;
} {
  if (!tcPr) return {};
  const tcW = firstChildNS(tcPr, "tcW");
  if (!tcW) return {};

  const wVal = wAttr(tcW, "w");
  const wType = wAttr(tcW, "type") ?? "dxa";
  if (!wVal || !/^\d+$/.test(wVal)) return {};

  const n = parseInt(wVal, 10);
  if (wType === "pct") return { widthPercent: Math.round(n / 50) };
  if (wType === "dxa") return { widthPx: twipsToPx(n) };
  return {};
}

function readCellVerticalAlign(
  tcPr: Element | null,
): OoxmlTableCellMeta["verticalAlign"] | undefined {
  if (!tcPr) return undefined;
  const vAlign = firstChildNS(tcPr, "vAlign");
  const val = vAlign ? wAttr(vAlign, "val") : null;
  switch (val) {
    case "center":
      return "middle";
    case "bottom":
      return "bottom";
    case "top":
      return "top";
    default:
      return undefined;
  }
}

function readCellShading(tcPr: Element | null): string | undefined {
  if (!tcPr) return undefined;
  const shd = firstChildNS(tcPr, "shd");
  const fill = shd ? wAttr(shd, "fill") : null;
  if (fill && fill !== "auto" && /^[0-9a-fA-F]{6}$/.test(fill)) {
    return `#${fill}`;
  }
  return undefined;
}

function readCellTextAlign(tcEl: Element): OoxmlTableCellMeta["textAlign"] {
  const paragraphs = tcEl.getElementsByTagNameNS(W_NS, "p");
  if (!paragraphs.length) return undefined;
  const pPr = firstChildNS(paragraphs[0], "pPr");
  const jcEl = pPr ? firstChildNS(pPr, "jc") : null;
  return jcToTextAlign(jcEl ? wAttr(jcEl, "val") : null);
}

function readTcColSpan(tcPr: Element | null): number {
  const gridSpan = tcPr ? firstChildNS(tcPr, "gridSpan") : null;
  const val = gridSpan ? wAttr(gridSpan, "val") : null;
  if (val && /^\d+$/.test(val)) return parseInt(val, 10);
  return 1;
}

function readVMergeContinue(tcPr: Element | null): boolean {
  const vMerge = tcPr ? firstChildNS(tcPr, "vMerge") : null;
  if (!vMerge) return false;
  const val = wAttr(vMerge, "val");
  return val === "continue" || val == null;
}

function readTableCellMeta(tcEl: Element): OoxmlTableCellMeta | null {
  const tcPr = firstChildNS(tcEl, "tcPr");
  if (readVMergeContinue(tcPr)) return null;

  const width = readCellWidth(tcPr);
  return {
    colSpan: readTcColSpan(tcPr),
    rowSpan: 1,
    widthPx: width.widthPx,
    widthPercent: width.widthPercent,
    textAlign: readCellTextAlign(tcEl),
    verticalAlign: readCellVerticalAlign(tcPr),
    backgroundColor: readCellShading(tcPr),
    borders: readCellBorders(tcPr),
  };
}

function computeRowSpans(rows: Element[]): Map<Element, number> {
  const rowSpanByCell = new Map<Element, number>();
  const columns: ({ cell: Element; rowSpan: number } | undefined)[] = [];

  rows.forEach((tr) => {
    const tcs = Array.from(tr.getElementsByTagNameNS(W_NS, "tc"));
    let cellIndex = 0;
    tcs.forEach((tc) => {
      const tcPr = firstChildNS(tc, "tcPr");
      const colSpan = readTcColSpan(tcPr);

      if (readVMergeContinue(tcPr)) {
        const col = columns[cellIndex];
        if (col) col.rowSpan++;
      } else {
        columns[cellIndex] = { cell: tc, rowSpan: 1 };
        rowSpanByCell.set(tc, 1);
      }
      cellIndex += colSpan;
    });
  });

  columns.forEach((col) => {
    if (col && col.rowSpan > 1) rowSpanByCell.set(col.cell, col.rowSpan);
  });

  return rowSpanByCell;
}

function readRowHeight(
  trPr: Element | null,
): Pick<OoxmlTableRowMeta, "heightPx" | "heightRule"> {
  const trHeight = trPr ? firstChildNS(trPr, "trHeight") : null;
  if (!trHeight) return {};

  const val = wAttr(trHeight, "val");
  if (!val || !/^\d+$/.test(val)) return {};

  const hRule = wAttr(trHeight, "hRule");
  let heightRule: OoxmlTableRowMeta["heightRule"];
  if (hRule === "atLeast") {
    heightRule = "atLeast";
  } else {
    heightRule = "exact";
  }

  return {
    heightPx: twipsToPx(parseInt(val, 10)),
    heightRule,
  };
}

function readTableRowMeta(
  trEl: Element,
  rowSpanByCell: Map<Element, number>,
): OoxmlTableRowMeta {
  const trPr = firstChildNS(trEl, "trPr");
  const tblHeader = trPr ? firstChildNS(trPr, "tblHeader") : null;
  const isHeader = !!tblHeader;
  const rowHeight = readRowHeight(trPr);

  const cells: OoxmlTableCellMeta[] = [];
  const tcs = trEl.getElementsByTagNameNS(W_NS, "tc");
  for (let i = 0; i < tcs.length; i++) {
    const meta = readTableCellMeta(tcs[i]);
    if (!meta) continue;
    meta.rowSpan = rowSpanByCell.get(tcs[i]) ?? 1;
    meta.isHeader = isHeader;
    cells.push(meta);
  }

  return {
    isHeader,
    heightPx: rowHeight.heightPx,
    heightRule: rowHeight.heightRule,
    cells,
  };
}

export function readTableMeta(tblEl: Element, index: number): OoxmlTableMeta {
  const tblPr = firstChildNS(tblEl, "tblPr");
  const width = readTableWidth(tblPr);
  const gridColWidthsPx = readGridColWidths(tblEl);
  const rows = Array.from(tblEl.getElementsByTagNameNS(W_NS, "tr"));
  const rowSpanByCell = computeRowSpans(rows);

  return {
    index,
    widthPercent: width.widthPercent,
    widthPx: width.widthPx,
    layoutMode: readTableLayout(tblPr),
    gridColWidthsPx: gridColWidthsPx.length ? gridColWidthsPx : undefined,
    borders: readTableBorders(tblPr),
    rows: rows.map((tr) => readTableRowMeta(tr, rowSpanByCell)),
  };
}

export function hasAnyBorders(borders?: CellBorderCss): boolean {
  if (!borders) return false;
  return !!(borders.top || borders.right || borders.bottom || borders.left);
}
